import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, MessageSquare, Printer, FileDown } from "lucide-react";
import { exportToExcel } from "@/lib/excel";
import { tableToPdf, whatsappSend } from "@/lib/pdf";
import { partyBalanceMessage } from "@/lib/messages";

export default function PartyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/parties/${id}/statement`);
      setData(data);
    } catch (e) { toast.error("Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const sendMsg = async () => {
    let firm = null;
    try { firm = (await api.get("/firms/me")).data; } catch {}
    const draft = partyBalanceMessage(data.party, data.net, firm);
    const body = window.prompt("Message to " + data.party.name, draft);
    if (!body) return;
    whatsappSend(data.party.phone, body);
  };

  const doPdf = () => tableToPdf({
    title: `Party Statement — ${data.party.name}`,
    subtitle: `Receivable ₹${data.receivable} · Payable ₹${data.payable} · Net ₹${data.net}`,
    columns: [
      { key: "date", label: "Date", w: 80 },
      { key: "txn_type", label: "Type", w: 90 },
      { key: "number", label: "Doc No", w: 100 },
      { key: "total", label: "Total", num: true, w: 80 },
      { key: "paid", label: "Paid", num: true, w: 70 },
      { key: "balance", label: "Balance", num: true, w: 80 },
    ],
    rows: data.transactions.map((t) => ({ ...t, date: new Date(t.date).toLocaleDateString() })),
    filename: `${data.party.name}-statement.pdf`,
  });

  if (!data) return <div className="text-sm text-muted-foreground">Loading...</div>;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 no-print">
        <Button variant="outline" size="sm" onClick={() => nav("/app/parties")}><ArrowLeft size={14} /> Back</Button>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={sendMsg} data-testid="party-detail-msg"><MessageSquare size={14} /> WhatsApp</Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="party-detail-print"><Printer size={14} /> Print</Button>
          <Button variant="outline" onClick={() => exportToExcel(data.transactions, `${data.party.name}-statement.xlsx`)} data-testid="party-detail-export"><FileDown size={14} /> Excel</Button>
          <Button variant="outline" onClick={doPdf} data-testid="party-detail-pdf"><FileDown size={14} /> PDF</Button>
        </div>
      </div>

      <div className="bg-white border border-border rounded-lg p-6 print-area">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="overline">Party Statement</div>
            <h1 className="text-3xl font-bold tracking-tight" data-testid="party-detail-name">{data.party.name}</h1>
            <div className="text-sm text-slate-600 mt-1">{data.party.phone} · {data.party.email} · {data.party.address}</div>
            {data.party.gstin && <div className="text-xs font-mono text-slate-500 mt-1">GSTIN: {data.party.gstin}</div>}
            {(data.party.sites || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {data.party.sites.map((s, i) => (<span key={i} className="chip chip-slate">Site: {s.name}</span>))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="border border-border rounded-md p-3 min-w-32">
              <div className="overline">Receivable</div>
              <div className="text-lg font-mono font-bold text-[#166534]" data-testid="pd-receivable">{fmtINR(data.receivable)}</div>
            </div>
            <div className="border border-border rounded-md p-3 min-w-32">
              <div className="overline">Payable</div>
              <div className="text-lg font-mono font-bold text-[#991B1B]" data-testid="pd-payable">{fmtINR(data.payable)}</div>
            </div>
            <div className="border-2 border-[#064E3B] rounded-md p-3 min-w-32">
              <div className="overline">Net Statement</div>
              <div className={`text-lg font-mono font-bold ${data.net >= 0 ? "text-[#166534]" : "text-[#991B1B]"}`} data-testid="pd-net">{fmtINR(data.net)}</div>
              <div className="text-[10px] text-muted-foreground">{data.net >= 0 ? "Receivable from party" : "Payable to party"}</div>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <div className="overline mb-2">Ledger</div>
          <table className="acc-table w-full">
            <thead><tr><th>Date</th><th>Type</th><th>Doc No</th><th>Site</th><th className="num">Total</th><th className="num">Paid</th><th className="num">Balance</th></tr></thead>
            <tbody>
              {data.transactions.map((t) => (
                <tr key={t._id_str}>
                  <td>{new Date(t.date).toLocaleDateString()}</td>
                  <td className="uppercase text-xs"><span className="chip chip-slate">{t.txn_type.replace("_", " ")}</span></td>
                  <td className="font-mono">{t.number}</td>
                  <td>{t.site || "—"}</td>
                  <td className="num">{fmtINR(t.total)}</td>
                  <td className="num">{fmtINR(t.paid)}</td>
                  <td className="num">{fmtINR(t.balance)}</td>
                </tr>
              ))}
              {data.transactions.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">No transactions.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
